import { type Chart, type Plugin } from "chart.js";

import { drawIcon } from "@features/sales-v3/utils/canvas";
import {
  CHART_PADDING,
  COMPARE_METRIC_1_ID,
  COMPARE_METRIC_1_LINE_DASH,
  COMPARE_METRIC_2_ID,
  COMPARE_METRIC_2_LINE_DASH,
  MAIN_METRIC_ID,
  buildCompareDateRangeData,
  buildDataset,
  calculateMetricValue,
  createCompare1BarChartPattern,
  createCompare2BarChartPattern,
  isLineChartCheck,
} from "@features/sales-v3/utils/chart";
import { type DateInterval } from "@north-beam/nb-common";

import {
  getChartDataManagerOptions,
  isIncomplete,
  type ChartDataManagerPluginOptions,
  type CustomChartDataset,
} from "./chart-data-manager-plugin";

const SPLIT_CHART_DATA_VIEW_PLUGIN_ID = "splitChartDataView";

const ICON_SIZE = 25;
const ICON_PADDING = 5;

export const splitChartDataViewPlugin: Plugin = {
  id: SPLIT_CHART_DATA_VIEW_PLUGIN_ID,
  beforeUpdate: (chart: Chart<any>) => {
    if (isMetricSplitConditionIsMet(chart)) {
      const chartDataManagerOptions = getChartDataManagerOptions(chart);
      chart.config.data = buildSplitMetricDataset(
        chartDataManagerOptions,
        isLineChartCheck(chart),
      );
    }
  },
  beforeElementsUpdate(chart: Chart<any>, _args: any, _options: any) {
    const datasets = chart.getSortedVisibleDatasetMetas();
    const datasetsWithIconsAndData = datasets.filter(
      ({ _dataset }: any) => _dataset.getIcon && _dataset.data.length > 0,
    );
    const isLineChart = isLineChartCheck(chart);
    const shouldDrawIcons = checkShouldDrawIconsInChart(chart);

    if (shouldDrawIcons && datasetsWithIconsAndData.length > 0) {
      if (isLineChart) {
        chart.config.options.scales.mainMetric.ticks.padding = 60;
        chart.config.options.layout.padding = {
          ...CHART_PADDING,
          left: -54,
        };
      } else {
        chart.config.options.scales.mainMetric.ticks.padding = 3;
        // top 64 is to account for the platform icons
        chart.config.options.layout.padding = { ...CHART_PADDING, top: 64 };
      }
    } else {
      chart.config.options.scales.mainMetric.ticks.padding = 3;
      chart.config.options.layout.padding = { ...CHART_PADDING };
    }
  },
  beforeDatasetsDraw(chart: Chart<any>, _args: any, _options: any) {
    const datasets = chart.getSortedVisibleDatasetMetas();
    const datasetsWithIconsAndData = datasets.filter(
      ({ _dataset }: any) => _dataset.getIcon && _dataset.data.length > 0,
    );
    const isLineChart = isLineChartCheck(chart);
    const shouldDrawIcons = checkShouldDrawIconsInChart(chart);

    if (shouldDrawIcons) {
      if (datasetsWithIconsAndData.length > 0 && isLineChart) {
        datasetsWithIconsAndData.forEach((dataset: any) => {
          drawIcon({
            chart,
            x: dataset.data[0].x - ICON_SIZE - 16,
            y: dataset.data[0].y - ICON_SIZE / 2,
            width: ICON_SIZE,
            height: ICON_SIZE,
            icon: dataset._dataset.getIcon({
              borderColor: dataset._dataset.baseBorderColor,
            }),
          });
        });
      } else {
        // Only draw one set of icons
        const mainMetricDatasetsWithIcons = datasetsWithIconsAndData.filter(
          ({ _dataset }: any) =>
            (_dataset as CustomChartDataset).realID === MAIN_METRIC_ID,
        );
        mainMetricDatasetsWithIcons.forEach((dataset: any, index: number) => {
          drawIcon({
            chart,
            x:
              chart.width -
              (ICON_SIZE + ICON_PADDING) *
                (mainMetricDatasetsWithIcons.length - index),
            y: 0,
            width: ICON_SIZE,
            height: ICON_SIZE,
            icon: dataset._dataset.getIcon({
              borderColor: dataset._dataset.baseBorderColor,
            }),
          });
        });
      }
    }
  },
};

export const isMetricSplitConditionIsMet = (chart: Chart<any>) => {
  const {
    selectedMetrics,
    chartConfig: { chartView },
  } = getChartDataManagerOptions(chart);
  return selectedMetrics.length <= 2 && chartView === "split";
};

const buildSplitMetricDataset = (
  {
    data,
    comparisonDatasets,
    selectedCompareMetrics,
    selectedMetrics,
    chartConfig: { comparisons },
    pageConfiguration: {
      compareDateRange,
      timeGranularity,
      timezone,
      dateRangeInterval,
      compareDateRangeInterval,
    },
    annotationDataset,
    labels,
    designElementManager,
    compareDateRangeLabels,
  }: ChartDataManagerPluginOptions,
  isLineChart: boolean,
) => {
  const [mainMetric, compare1, compare2] = selectedMetrics;
  const datasets =
    data.datasets.reduce<CustomChartDataset[]>((acc, child) => {
      const designElement = designElementManager.getDesignElement(
        child.name || "",
      );

      const baseColor = designElement.iconColor;
      const transparentColor = designElement.iconColor + "50";

      const lineBorderColor = baseColor;
      const lineBackgroundColor = baseColor;

      const isIncompleteMain = (date: Date, index: number) =>
        isIncomplete({
          date,
          dateRange: dateRangeInterval,
          timeGranularity,
          index,
          total: labels.length,
        });

      const barBorderColor = labels.map((date, index) =>
        isIncompleteMain(date, index) ? transparentColor : baseColor,
      );
      const barBackgroundColor = barBorderColor;

      const mainDataset = buildDataset({
        ...designElement,
        data: labels.map((date) =>
          calculateMetricValue(mainMetric, child.series[date.toISOString()]),
        ),
        dimensions: child.dimensions,
        baseBorderColor: baseColor,
        baseBackgroundColor: baseColor,
        borderColor: isLineChart ? lineBorderColor : barBorderColor,
        backgroundColor: isLineChart ? lineBackgroundColor : barBackgroundColor,
        yAxisID: MAIN_METRIC_ID,
        realID: MAIN_METRIC_ID,
        name: child.name ?? "N/A",
        borderDash: [],
        segment: {
          borderColor: (ctx: any) =>
            isIncompleteMain(labels[ctx.p0DataIndex], ctx.p0DataIndex) ||
            isIncompleteMain(labels[ctx.p1DataIndex], ctx.p1DataIndex)
              ? transparentColor
              : baseColor,
        },
      });
      acc.push(mainDataset);

      return acc;
    }, []) ?? [];

  comparisons.forEach((compare, index) => {
    comparisonDatasets[index]?.datasets.forEach((comparisonDataset) => {
      const designElement = designElementManager.getDesignElement(
        comparisonDataset.name || "",
      );

      if (index === 0) {
        const metricToUse =
          compare1 || selectedCompareMetrics?.[0] || mainMetric;
        const usingInferredMetric = selectedCompareMetrics?.[0] === metricToUse;

        const baseColor = designElement.iconColor;
        const transparentColor = designElement.iconColor + "50";

        const baseBorderColor = baseColor;
        const baseBackgroundColor = createCompare1BarChartPattern(baseColor);

        const lineBorderColor = baseColor;
        const lineBackgroundColor = baseColor;

        const compareLabels =
          compare.queryKey === "compareDateRange"
            ? compareDateRangeLabels || []
            : labels;

        const isIncompleteCompare1 = (date: Date, index: number) =>
          isIncomplete({
            date,
            dateRange:
              compare.queryKey === "compareDateRange"
                ? compareDateRangeInterval
                : dateRangeInterval,
            timeGranularity,
            index,
            total: compareLabels.length,
          });

        const barBorderColor = compareLabels.map((date, index) =>
          isIncompleteCompare1(date, index) ? transparentColor : baseColor,
        );
        const barBackgroundColor = compareLabels.map((date, index) =>
          isIncompleteCompare1(date, index)
            ? createCompare1BarChartPattern(transparentColor)
            : createCompare1BarChartPattern(baseColor),
        );

        const compare1Dataset = buildDataset({
          ...designElement,
          data:
            compare.queryKey === "compareDateRange"
              ? buildCompareDateRangeData({
                  labels,
                  metricSeries: comparisonDataset.series,
                  metric: metricToUse,
                  compareDateRange: compareDateRange as DateInterval,
                  timeGranularity,
                  timezone,
                })
              : labels.map((date) =>
                  calculateMetricValue(
                    metricToUse,
                    comparisonDataset.series[date.toISOString()],
                  ),
                ),
          yAxisID: usingInferredMetric ? MAIN_METRIC_ID : COMPARE_METRIC_1_ID,
          dimensions: comparisonDataset.dimensions,
          realID: COMPARE_METRIC_1_ID,
          baseBorderColor,
          baseBackgroundColor,
          borderColor: isLineChart ? lineBorderColor : barBorderColor,
          createBackgroundColor: createCompare1BarChartPattern,
          backgroundColor: isLineChart
            ? lineBackgroundColor
            : barBackgroundColor,
          borderDash: COMPARE_METRIC_1_LINE_DASH,
          segment: {
            borderColor: (ctx: any) =>
              isIncompleteCompare1(
                compareLabels[ctx.p0DataIndex],
                ctx.p0DataIndex,
              ) ||
              isIncompleteCompare1(
                compareLabels[ctx.p1DataIndex],
                ctx.p1DataIndex,
              )
                ? transparentColor
                : baseColor,
          },
          name: comparisonDataset.name ?? "",
          compare,
        });
        datasets.push(compare1Dataset);
      } else if (index === 1) {
        const metricToUse =
          compare2 || selectedCompareMetrics?.[1] || mainMetric;
        const usingInferredMetric = selectedCompareMetrics?.[1] === metricToUse;

        const baseColor = designElement.iconColor;
        const transparentColor = designElement.iconColor + "50";

        const baseBorderColor = baseColor;
        const baseBackgroundColor = createCompare2BarChartPattern(baseColor);

        const lineBorderColor = baseColor;
        const lineBackgroundColor = baseColor;

        const compareLabels =
          compare.queryKey === "compareDateRange"
            ? compareDateRangeLabels || []
            : labels;

        const isIncompleteCompare2 = (date: Date, index: number) =>
          isIncomplete({
            date,
            dateRange:
              compare.queryKey === "compareDateRange"
                ? compareDateRangeInterval
                : dateRangeInterval,
            timeGranularity,
            index,
            total: compareLabels.length,
          });

        const barBorderColor = compareLabels.map((date, index) =>
          isIncompleteCompare2(date, index) ? transparentColor : baseColor,
        );
        const barBackgroundColor = compareLabels.map((date, index) =>
          isIncompleteCompare2(date, index)
            ? createCompare2BarChartPattern(transparentColor)
            : createCompare2BarChartPattern(baseColor),
        );

        const compare2Dataset = buildDataset({
          ...designElement,
          data:
            compare.queryKey === "compareDateRange"
              ? buildCompareDateRangeData({
                  labels,
                  metricSeries: comparisonDataset.series,
                  metric: metricToUse,
                  compareDateRange: compareDateRange as DateInterval,
                  timeGranularity,
                  timezone,
                })
              : labels.map((date) =>
                  calculateMetricValue(
                    metricToUse,
                    comparisonDataset.series[date.toISOString()],
                  ),
                ),
          yAxisID: usingInferredMetric ? MAIN_METRIC_ID : COMPARE_METRIC_2_ID,
          dimensions: comparisonDataset.dimensions,
          realID: COMPARE_METRIC_2_ID,
          baseBackgroundColor,
          baseBorderColor,
          borderColor: isLineChart ? lineBorderColor : barBorderColor,
          createBackgroundColor: createCompare2BarChartPattern,
          backgroundColor: isLineChart
            ? lineBackgroundColor
            : barBackgroundColor,
          borderDash: COMPARE_METRIC_2_LINE_DASH,
          segment: {
            borderColor: (ctx: any) =>
              isIncompleteCompare2(
                compareLabels[ctx.p0DataIndex],
                ctx.p0DataIndex,
              ) ||
              isIncompleteCompare2(
                compareLabels[ctx.p1DataIndex],
                ctx.p1DataIndex,
              )
                ? transparentColor
                : baseColor,
          },
          name: comparisonDataset.name ?? "",
          compare,
        });
        datasets.push(compare2Dataset);
      }
    });
  });

  annotationDataset &&
    datasets.push(annotationDataset as unknown as CustomChartDataset);

  return { labels, datasets };
};

export const checkShouldDrawIconsInChart = (chart: Chart<any>) => {
  const { pageConfiguration, activeTab } = getChartDataManagerOptions(chart);
  const isPlatformFirstBreakdown =
    pageConfiguration.breakdowns?.[0]?.key === "Platform (Northbeam)";
  const isBreakdownTab = activeTab === "breakdown";
  const hasNoBreakdowns = pageConfiguration.breakdowns?.length === 0;
  return (
    (isBreakdownTab && isPlatformFirstBreakdown) ||
    (isBreakdownTab && hasNoBreakdowns)
  );
};

export const checkShouldDrawChartLegendInChart = (chart: Chart<any>) =>
  !checkShouldDrawIconsInChart(chart);
